import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Typography, Input, Button, Tag, Tooltip } from 'antd';
import { SearchOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { api } from '../../services/api';
import { useClient } from '../../context/ClientContext';
import { SkStyleTag, skeletonColumns, skeletonRows } from '../../shared/components/TableSkeleton';
import { ClienteFormModal } from '../../shared/components/ClienteFormModal';
import { hexAlpha } from '../../utils/colorUtils';

const { Text } = Typography;

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-AR') : null);

export const Clientes = () => {
  const { theme, clientPath } = useClient();
  const navigate = useNavigate();
  const primary = theme?.primaryColor || '#111827';

  const [clientes, setClientes] = useState([]);
  const [listas, setListas] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [alta, setAlta] = useState(false);

  const cargar = useCallback(async (texto = '') => {
    setLoading(true);
    try {
      const res = await api.admin.clientes(texto);
      if (res.status === 'success') setClientes(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    api.admin.listasVenta().then((res) => {
      if (res.status === 'success') setListas(res.data || []);
    });
  }, [cargar]);

  const nombreLista = useMemo(() => {
    const m = {};
    listas.forEach((l) => { m[l.id] = l.nombre; });
    return m;
  }, [listas]);

  const columns = [
    {
      title: 'Cliente', key: 'cliente', minWidth: 220,
      render: (_, c) => {
        const iniciales = (c.nombre || '?').split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 8, flexShrink: 0,
              background: hexAlpha(primary, 0.10), color: primary,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13,
            }}>{iniciales}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: primary }}>{c.nombre}</div>
              {c.mail && <div style={{ fontSize: 12, color: '#9ca3af' }}>{c.mail}</div>}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Documento', key: 'doc', width: 170,
      render: (_, c) => (
        <div style={{ fontSize: 13 }}>
          {c.dni && <div>DNI {c.dni}</div>}
          {c.cuit && <div>CUIT {c.cuit}</div>}
          {!c.dni && !c.cuit && <Text type="secondary">—</Text>}
        </div>
      ),
    },
    {
      title: 'Teléfono', dataIndex: 'telefono', key: 'telefono', width: 140,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Lista', dataIndex: 'idpreciolista', key: 'lista', width: 120,
      render: (v) => <Tag>{nombreLista[v] || 'Minorista'}</Tag>,
    },
    {
      title: 'Pedidos', key: 'pedidos', width: 130,
      render: (_, c) => (
        <div style={{ fontSize: 13 }}>
          <div>{c.pedidos}</div>
          {c.ultimo_pedido && <Text type="secondary" style={{ fontSize: 12 }}>último {fecha(c.ultimo_pedido)}</Text>}
        </div>
      ),
    },
    {
      title: 'Origen', key: 'origen', width: 150,
      render: (_, c) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {c.usuario_web && (
            <Tooltip title={c.usuario_web}><Tag color="blue">Cuenta web</Tag></Tooltip>
          )}
          {c.idcliente_erp && <Tag>ERP #{c.idcliente_erp}</Tag>}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Text strong style={{ fontSize: 28, lineHeight: 1.2, display: 'block' }}>Clientes</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {loading ? ' ' : `${clientes.length} cliente${clientes.length === 1 ? '' : 's'}`}
          </Text>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            placeholder="Nombre, DNI, CUIT o mail"
            value={q}
            onChange={(e) => { setQ(e.target.value); if (!e.target.value) cargar(); }}
            onPressEnter={() => cargar(q)}
            allowClear
            style={{ width: 260 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => cargar(q)}>Actualizar</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAlta(true)}>Nuevo cliente</Button>
        </div>
      </div>

      <SkStyleTag />
      <Table
        columns={loading ? skeletonColumns(columns, { avatarIndex: 0, twoLineIndexes: [0] }) : columns}
        dataSource={loading ? skeletonRows(8) : clientes}
        rowKey={loading ? 'key' : 'id'}
        size="middle"
        scroll={{ x: 'max-content' }}
        onRow={loading ? undefined : (c) => ({
          onClick: () => navigate(clientPath(`/admin/clientes/${c.id}`)),
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: q ? 'Ningún cliente coincide con la búsqueda.' : 'Todavía no hay clientes.' }}
        pagination={loading ? false : { pageSize: 20, showSizeChanger: false, style: { padding: '8px 16px' } }}
        style={{ background: '#fff', borderRadius: 8 }}
      />

      <ClienteFormModal
        open={alta}
        onClose={() => setAlta(false)}
        onCreado={(cliente) => { setAlta(false); navigate(clientPath(`/admin/clientes/${cliente.id}`)); }}
        accionExistente={{
          label: 'Abrir cliente',
          onClick: (c) => navigate(clientPath(`/admin/clientes/${c.id}`)),
        }}
      />
    </div>
  );
};
